import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ProfileAvatarImage from '@/components/ProfileAvatarImage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Users, Check, X, Clock, Loader2, Send, Trash2, Flag, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { API_URL } from '@/lib/api';
const DEFAULT_AVATAR = '/shh-logo-new.jpeg';

const getAvatarUrl = (pic) => {
  if (!pic) return DEFAULT_AVATAR;
  if (pic.startsWith('/api/')) return `${API_URL}${pic}`;
  return pic;
};

export default function ConfidantsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [confidants, setConfidants] = useState([]);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reporting, setReporting] = useState(false);
  const [processing, setProcessing] = useState({});

  

  const fetchData = async () => {
    try {
      const [reqRes, confRes] = await Promise.all([
        axios.get(`${API_URL}/api/confidants/requests`),
        axios.get(`${API_URL}/api/confidants`)
      ]);
      setRequests(reqRes.data);
      setConfidants(confRes.data.confidants);
    } catch (error) {
      console.error('Error fetching confidants data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAccept = async (requestId) => {
    setProcessing(p => ({ ...p, [requestId]: 'accept' }));
    try {
      await axios.post(`${API_URL}/api/confidants/requests/${requestId}/accept`);
      toast.success('Confidant request accepted!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    } finally {
      setProcessing(p => ({ ...p, [requestId]: null }));
    }
  };

  const handleDeny = async (requestId) => {
    setProcessing(p => ({ ...p, [requestId]: 'deny' }));
    try {
      await axios.post(`${API_URL}/api/confidants/requests/${requestId}/deny`);
      toast.success('Request denied');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny');
    } finally {
      setProcessing(p => ({ ...p, [requestId]: null }));
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await axios.delete(`${API_URL}/api/confidants/${removeTarget.user_id}`);
      toast.success(`${removeTarget.display_name} removed from your confidants`);
      setRemoveTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    } finally {
      setRemoving(false);
    }
  };

  const handleReport = async () => {
    if (!reportTarget) return;
    setReporting(true);
    try {
      await axios.post(`${API_URL}/api/confidants/requests/${reportTarget.request_id}/report`, { reason: reportReason || null });
      toast.success('Request reported to admins');
      setReportTarget(null);
      setReportReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report');
    } finally {
      setReporting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="confidants-page-loading">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="confidants-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="flex items-center gap-3 mb-6">
          <Users className="w-6 h-6 text-forest-600 dark:text-forest-400" />
          <h1 className="font-serif text-2xl md:text-3xl font-bold">Confidants</h1>
        </div>

        {/* Incoming Requests */}
        {requests.incoming.length > 0 && (
          <div className="mb-8" data-testid="incoming-requests">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Pending Requests ({requests.incoming.length})
            </h2>
            <div className="space-y-3">
              {requests.incoming.map((req) => (
                <Card key={req.request_id} className="border-amber-200/50 dark:border-amber-800/30" data-testid={`request-${req.request_id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                      <Avatar
                        className="w-10 h-10 cursor-pointer shrink-0"
                        onClick={() => navigate(`/profile/${req.sender_id}`)}
                      >
                        <ProfileAvatarImage
                          src={getAvatarUrl(req.sender_profile_picture)}
                          alt={req.sender_display_name}
                          zoom={req.sender_profile_picture_zoom}
                          positionX={req.sender_profile_picture_position_x}
                          positionY={req.sender_profile_picture_position_y}
                        />
                        <AvatarFallback><img src={DEFAULT_AVATAR} alt="" className="w-full h-full object-cover" /></AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">
                          <span
                            className="cursor-pointer hover:underline"
                            onClick={() => navigate(`/profile/${req.sender_id}`)}
                          >
                            {req.sender_display_name}
                          </span>
                          <span className="text-muted-foreground font-normal"> wants to be your confidant</span>
                        </p>
                        {req.message && (
                          <p className="text-sm text-foreground/70 mt-1 italic bg-muted/50 rounded px-2 py-1">
                            "{req.message}"
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => handleAccept(req.request_id)}
                            disabled={!!processing[req.request_id]}
                            data-testid={`accept-${req.request_id}`}
                          >
                            {processing[req.request_id] === 'accept' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => handleDeny(req.request_id)}
                            disabled={!!processing[req.request_id]}
                            data-testid={`deny-${req.request_id}`}
                          >
                            {processing[req.request_id] === 'deny' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <X className="w-3 h-3 mr-1" />}
                            Deny
                          </Button>
                          {req.message && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-red-500 hover:text-red-600"
                              onClick={() => setReportTarget(req)}
                              data-testid={`report-${req.request_id}`}
                            >
                              <Flag className="w-3 h-3 mr-1" />
                              Report
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Outgoing Requests */}
        {requests.outgoing.length > 0 && (
          <div className="mb-8" data-testid="outgoing-requests">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Sent Requests ({requests.outgoing.length})
            </h2>
            <div className="space-y-2">
              {requests.outgoing.map((req) => (
                <Card key={req.request_id} className="bg-muted/30" data-testid={`outgoing-${req.request_id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center gap-3">
                      <Avatar
                        className="w-8 h-8 cursor-pointer"
                        onClick={() => navigate(`/profile/${req.receiver_id}`)}
                      >
                        <ProfileAvatarImage
                          src={getAvatarUrl(req.receiver_profile_picture)}
                          alt={req.receiver_display_name}
                          zoom={req.receiver_profile_picture_zoom}
                          positionX={req.receiver_profile_picture_position_x}
                          positionY={req.receiver_profile_picture_position_y}
                        />
                        <AvatarFallback><img src={DEFAULT_AVATAR} alt="" className="w-full h-full object-cover" /></AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <span
                          className="text-sm font-medium cursor-pointer hover:underline"
                          onClick={() => navigate(`/profile/${req.receiver_id}`)}
                        >
                          {req.receiver_display_name}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        <Clock className="w-3 h-3 mr-1" /> Pending
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Confidants List */}
        <div data-testid="confidants-list">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Your Confidants ({confidants.length})
          </h2>
          {confidants.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No confidants yet</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Visit someone's profile and send a confidant request to connect.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {confidants.map((c) => (
                <Card key={c.user_id} className="hover:bg-muted/30 transition-colors" data-testid={`confidant-${c.user_id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center gap-3">
                      <Avatar
                        className="w-10 h-10 cursor-pointer"
                        onClick={() => navigate(`/profile/${c.user_id}`)}
                      >
                        <ProfileAvatarImage
                          src={getAvatarUrl(c.profile_picture)}
                          alt={c.display_name}
                          zoom={c.profile_picture_zoom}
                          positionX={c.profile_picture_position_x}
                          positionY={c.profile_picture_position_y}
                        />
                        <AvatarFallback><img src={DEFAULT_AVATAR} alt="" className="w-full h-full object-cover" /></AvatarFallback>
                      </Avatar>
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => navigate(`/profile/${c.user_id}`)}
                      >
                        <p className="font-medium text-sm hover:underline">{c.display_name}</p>
                        {c.since && (
                          <p className="text-xs text-muted-foreground">
                            Since {format(new Date(c.since), 'MMM d, yyyy')}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-8"
                        onClick={() => setRemoveTarget(c)}
                        data-testid={`remove-confidant-${c.user_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Remove Confirmation Dialog */}
      <Dialog open={!!removeTarget} onOpenChange={() => setRemoveTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove Confidant?</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{removeTarget?.display_name}</strong> as a confidant? 
              This will remove the relationship for both of you. They will no longer see your private information.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={removing}
              data-testid="confirm-remove-confidant"
            >
              {removing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={!!reportTarget} onOpenChange={() => { setReportTarget(null); setReportReason(''); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Report Request Message</DialogTitle>
            <DialogDescription>
              Report the message from <strong>{reportTarget?.sender_display_name}</strong> to admins for review.
            </DialogDescription>
          </DialogHeader>
          {reportTarget?.message && (
            <div className="bg-muted/50 rounded p-3 text-sm italic">"{reportTarget.message}"</div>
          )}
          <div>
            <label className="text-sm font-medium mb-1 block">Reason (optional)</label>
            <textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Why is this message offensive?"
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="report-reason-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setReportTarget(null); setReportReason(''); }}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReport}
              disabled={reporting}
              data-testid="confirm-report-btn"
            >
              {reporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
              Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
